import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PRICE_SEN, kategoriFor } from "@jomcerdik/shared";
import { Art } from "../ui/Art";
import { Banner, SectionTitle, pastel } from "../ui/kit";
import { formatMYR } from "../lib/format";
import { api } from "../lib/api";
import { t, useLang } from "../lib/i18n";

type Remaining = { tier1: number; tier2: number };
const rm = (sen: number) => formatMYR(sen).replace(".00", "");

export function LandingPage() {
  const KATEGORI = kategoriFor(useLang() === "ms" ? "ms" : "en");
  const [left, setLeft] = useState<Remaining | null>(null);
  useEffect(() => {
    api<{ earlyRemaining: Remaining }>("/api/config/public")
      .then((r) => setLeft(r.earlyRemaining))
      .catch(() => setLeft(null)); // no fabricated numbers if the API is unreachable
  }, []);

  return (
    <>
      <Banner art="cover/belajar-hero" kicker="Jom" title="Cerdik" tagline={t("Belajar · Bermain · Jadi Lebih Bijak")} />
      <div className="mb-4 text-center">
        <h1 className="text-2xl font-black text-slate-900 sm:text-4xl">{t("Belajar dengan lebih seronok bersama JomCerdik")}</h1>
        <p className="mx-auto mt-1 max-w-2xl text-base text-slate-600">{t("Nota, audio, aktiviti, kuiz dan kertas UASA untuk Tadika hingga Tahun 6.")}</p>
        <div className="mt-3 flex flex-wrap justify-center gap-3">
          <Link to="/belajar" className="inline-flex min-h-12 items-center rounded-full bg-emerald-500 px-7 text-base font-extrabold text-white shadow-md">{t("Mula belajar")}</Link>
          <Link to="/beli" className="inline-flex min-h-12 items-center rounded-full bg-white px-7 text-base font-extrabold text-brand shadow-sm ring-1 ring-brand/20">{t("Harga & pembelian")}</Link>
        </div>
      </div>
      <SiteSearch />
      <SectionTitle>{t("Akses pantas ke modul pembelajaran")}</SectionTitle>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {QUICK.map((q, n) => (
          <Link key={q.to} to={q.to} className={`${pastel(n)} group overflow-hidden rounded-3xl text-center shadow-md transition hover:-translate-y-1 active:scale-95`}>
            <div className="grid aspect-[4/3] place-items-center overflow-hidden">
              {q.art.startsWith("cover/") ? <Art id={q.art} alt="" className="h-full w-full object-cover transition group-hover:scale-105" /> : <Art id={q.art} alt="" className="h-28 w-28 transition group-hover:scale-110" />}
            </div>
            <div className="bg-white p-3">
              <span className="block text-lg font-black leading-tight text-slate-900">{t(q.label)}</span>
              <span className="block min-h-8 text-xs leading-snug text-slate-600">{t(q.text)}</span>
            </div>
          </Link>
        ))}
      </div>
      <Link to="/belajar/permainan/fotosintesis" className="group mt-3 flex items-center gap-4 overflow-hidden rounded-3xl bg-gradient-to-r from-lime-500 to-emerald-700 p-3 text-white shadow-md transition hover:-translate-y-1 active:scale-[0.99]">
        <Art id="alam/pokok" alt="" className="h-20 w-20 shrink-0 transition group-hover:scale-110" />
        <span className="min-w-0 flex-1">
          <span className="inline-block rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-emerald-800">{t("Baru")}</span>
          <span className="block text-lg font-black leading-tight">{t("Fotosintesis")}</span>
          <span className="block text-xs leading-snug text-white/90">{t("Beri pokok air, CO₂ dan cahaya matahari. Lihat ia membesar.")}</span>
        </span>
        <span className="shrink-0 rounded-full bg-white px-4 py-1.5 text-sm font-extrabold text-emerald-800">{t("Main")}</span>
      </Link>
      <Link to="/belajar/permainan/angkasa" className="group mt-3 flex items-center gap-4 overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 to-slate-900 p-3 text-white shadow-md transition hover:-translate-y-1 active:scale-[0.99]">
        <Art id="permainan/angkasa" alt="" className="h-20 w-20 shrink-0 rounded-2xl object-cover transition group-hover:scale-110" />
        <span className="min-w-0 flex-1">
          <span className="inline-block rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-indigo-800">{t("Baru")}</span>
          <span className="block text-lg font-black leading-tight">{t("Space Math")}</span>
          <span className="block text-xs leading-snug text-white/90">{t("Gerakkan kapal dan tembak batu angkasa yang ada jawapan betul. Tahun 1–6.")}</span>
        </span>
        <span className="shrink-0 rounded-full bg-white px-4 py-1.5 text-sm font-extrabold text-indigo-800">{t("Main")}</span>
      </Link>
      <SectionTitle>{t("Kategori Tadika")}</SectionTitle>
      <div className="flex flex-wrap gap-2">
        {KATEGORI.map((c, n) => <Link key={c.id} to={`/belajar/tadika/kategori/${c.id}`} className={`${pastel(n)} flex items-center gap-2 rounded-full py-1.5 pl-1.5 pr-4 text-sm font-bold text-slate-800 shadow-sm`}><Art id={`cover/${c.id}`} alt="" className="h-9 w-9 rounded-full object-cover" />{c.ms}</Link>)}
      </div>
      <div className="original-page">
      <section className="mt-8" aria-labelledby="harga">
        <h2 id="harga" className="text-lg font-semibold">{t("Harga awal untuk pembeli terawal")}</h2>
        <ol className="mt-3 grid gap-3 sm:grid-cols-3">
          <PriceStep price={PRICE_SEN.EARLY_BIRD_1} label={t("100 pembeli pertama")} remaining={left?.tier1} />
          <PriceStep price={PRICE_SEN.DIRECT} label={t("Pembeli ke-101 hingga 200")} remaining={left?.tier2} />
          <PriceStep price={PRICE_SEN.STANDARD} label={t("Harga biasa selepas itu")} />
        </ol>
        <p className="mt-3 text-sm text-muted">
          {t("Harga {p1} dan {p2} hanya untuk pembelian terus di sini. Hanya 200 pembeli awal boleh menjadi affiliate: rakan anda beli pada {p3}, dan anda dapat {c1} (jika anda beli {p1}) atau {c2} (jika anda beli {p2}) bagi setiap jualan.", {
            p1: rm(PRICE_SEN.EARLY_BIRD_1), p2: rm(PRICE_SEN.DIRECT), p3: rm(PRICE_SEN.STANDARD),
            c1: rm(PRICE_SEN.STANDARD - PRICE_SEN.EARLY_BIRD_1), c2: rm(PRICE_SEN.STANDARD - PRICE_SEN.DIRECT),
          })}
        </p>
      </section>
      </div>
    </>
  );
}

const QUICK = [
  { to: "/belajar/tadika", label: "Tadika", text: "Huruf, nombor, warna dan banyak lagi", art: "cover/tadika-hero" },
  { to: "/belajar/rendah/1", label: "Tahun 1–3", text: "Nota, latihan dan kuiz", art: "subjek/matematik" },
  { to: "/belajar/rendah/4", label: "Tahun 4–6", text: "Nota, latihan dan kuiz", art: "subjek/sains" },
  { to: "/uasa", label: "UASA", text: "Penjana kertas dan skema", art: "cover/uasa-hero" },
  { to: "/belajar/permainan", label: "Permainan", text: "Belajar sambil bermain", art: "permainan/padan" },
  { to: "/affiliate", label: "Affiliate", text: "Kongsi dan dapat komisen", art: "cover/affiliate" },
] as const;

/** Searches the menu and the Tadika categories; Enter opens the first match. */
function SiteSearch() {
  const KATEGORI = kategoriFor(useLang() === "ms" ? "ms" : "en");
  const [q, setQ] = useState("");
  const go = useNavigate();
  const entries = [
    ...QUICK.map((x) => ({ to: x.to, label: t(x.label) })),
    { to: "/belajar/permainan/fotosintesis", label: t("Fotosintesis") },
    { to: "/belajar/permainan/angkasa", label: t("Space Math") },
    { to: "/login", label: t("Akaun") }, { to: "/beli", label: t("Harga dan pembelian") }, { to: "/redeem", label: t("Tebus kod") },
    ...KATEGORI.map((c) => ({ to: `/belajar/tadika/kategori/${c.id}`, label: `Tadika: ${c.ms}` })),
  ];
  const hits = q.trim() ? entries.filter((e) => e.label.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6) : [];
  return (
    <form role="search" className="relative" onSubmit={(e) => { e.preventDefault(); if (hits[0]) go(hits[0].to); }}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Cari modul, subjek atau kategori…")} aria-label={t("Cari")}
        className="min-h-12 w-full rounded-full border border-surface-line bg-card px-5 text-base text-slate-900 shadow-sm" />
      {hits.length > 0 && (
        <ul className="absolute left-0 right-0 z-20 mt-2 overflow-hidden rounded-2xl border border-surface-line bg-card shadow-lg">
          {hits.map((h) => <li key={h.to}><Link to={h.to} className="block px-5 py-3 font-semibold text-slate-800 hover:bg-sky-50">{h.label}</Link></li>)}
        </ul>
      )}
    </form>
  );
}

function PriceStep({ price, label, remaining }: { price: number; label: string; remaining?: number | undefined }) {
  const soldOut = remaining === 0;
  return (
    <li className={`rounded-card border p-4 ${soldOut ? "border-surface-line opacity-60" : "border-accent"}`}>
      <p className={`text-2xl font-bold ${soldOut ? "line-through" : ""}`}>{rm(price)}</p>
      <p className="text-sm">{label}</p>
      {remaining !== undefined && <p className="mt-1 text-xs text-muted">{soldOut ? t("Habis") : t("Tinggal {n} slot", { n: remaining })}</p>}
    </li>
  );
}

